import { createContext, useContext } from 'react';

import type { AdminMe, TokenPair } from '@/api/types';

import type { SessionEndReason } from './session';

/**
 * - `loading` — tokens exist, `/api/admin/me/` is being fetched;
 * - `authenticated` — `me` is known;
 * - `anonymous` — no session;
 * - `error` — tokens exist but the profile could not be loaded (network/server failure).
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  me: AdminMe | null;
  /** Why the previous session ended; drives the `/login?blocked=1|expired=1` redirect. */
  endReason: SessionEndReason | null;
  /** Stores the tokens of a successful sign-in; pass `me` from the verify response to skip a fetch. */
  login: (tokens: TokenPair, me?: AdminMe) => void;
  /** Signs out in every tab and revokes the refresh token server-side. */
  logout: () => void;
  /** Re-fetches `/api/admin/me/`. */
  refreshMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
